import {
  DynamicStringSchema,
  DynamicValueSchema,
  ActionSchema,
} from '@a2ui/web_core/v0_9';
import { createComponentImplementation } from '@a2ui/react/v0_9';
import { defineA2uiComponentSchema } from '../../a2ui-schema';
import { EmailComposer, type EmailAttachment } from './EmailComposer';

interface ResolvedProps {
  readonly to?: string;
  readonly cc?: string;
  readonly bcc?: string;
  readonly subject?: string;
  readonly body?: string;
  readonly attachments?: readonly EmailAttachment[];
  readonly onSend?: () => void;
  readonly onCopy?: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const schema = defineA2uiComponentSchema({
  to: DynamicStringSchema,
  cc: DynamicStringSchema,
  bcc: DynamicStringSchema,
  subject: DynamicStringSchema,
  body: DynamicStringSchema,
  // Read-only generated-file attachments ({artifactId, fileName, sizeBytes})
  // that ride the email on Send — array-valued, so DynamicValueSchema.
  attachments: DynamicValueSchema,
  onSend: ActionSchema,
  onCopy: ActionSchema,
}) as any;

export const EmailComposerImpl = createComponentImplementation(
  { name: 'EmailComposer', schema },
  // The v0_9 catalog runtime hands us `context.dataContext`, a scoped view of
  // the surface's DataModel. `dataContext.set(path, value)` pushes user input
  // back at the given JSON pointer — this is what wires the editable inputs
  // to the agent-emitted /to, /subject, /body slots so subsequent reads (e.g.
  // the email_composer_send action handler) see the user's edits.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ({
    props,
    context,
  }: {
    props: ResolvedProps;
    buildChild: any;
    context: any;
  }) => {
    const writeAt = (path: string, value: string) => {
      context.dataContext.set(path, value);
    };
    // Attachments are display-only: tolerate a missing/odd-shaped binding
    // (older agents emit no /attachments slot) by rendering none. Require
    // BOTH fields EmailAttachment needs — the chip list keys on artifactId,
    // so an entry with fileName but no artifactId would render with an
    // undefined key and reconcile incorrectly alongside a sibling chip.
    const attachments = Array.isArray(props.attachments)
      ? props.attachments.filter(
          (a): a is EmailAttachment =>
            !!a &&
            typeof a === 'object' &&
            typeof (a as Record<string, unknown>).artifactId === 'string' &&
            typeof (a as Record<string, unknown>).fileName === 'string'
        )
      : [];
    return (
      <EmailComposer
        to={props.to}
        cc={props.cc}
        bcc={props.bcc}
        subject={props.subject}
        body={props.body}
        attachments={attachments}
        onToChange={(v) => writeAt('/to', v)}
        onCcChange={(v) => writeAt('/cc', v)}
        onBccChange={(v) => writeAt('/bcc', v)}
        onSubjectChange={(v) => writeAt('/subject', v)}
        onBodyChange={(v) => writeAt('/body', v)}
        onSend={props.onSend}
        onCopy={props.onCopy}
      />
    );
  }
);
